import { Decisions, getAutoshipMaxSavings } from '@libs/common-next';
import { AUTOSHIP_FREE_SHIPPING_THRESHOLD, AUTOSHIP_RECURRING_ORDERS_DISCOUNT } from '@libs/core';

type GetAutoshipDiscountMessagesParams = {
    isFirstAutoshipEligible: boolean;
    autoshipFlag: Decisions['ff_autoship'];
    discountDisplay?: string;
};

export function getAutoshipDiscountMessages({
    autoshipFlag,
    discountDisplay,
    isFirstAutoshipEligible,
}: GetAutoshipDiscountMessagesParams) {
    // If there is no discount display the SKU is most likely
    // MAP restricted, so the generic message should be returned
    if (!discountDisplay?.trim()) {
        return {
            autoshipDiscountHeader: 'Subscribe with Autoship',
            autoshipDiscountSubtext: `Future orders ship on your schedule & get FREE shipping over ${AUTOSHIP_FREE_SHIPPING_THRESHOLD}.`,
            autoshipDiscountSubtextTwo: undefined,
        };
    }

    const shouldShowRecurringDiscount = !isFirstAutoshipEligible;
    const recurringDiscount = shouldShowRecurringDiscount ? AUTOSHIP_RECURRING_ORDERS_DISCOUNT : null;
    const displayDiscount = recurringDiscount ?? discountDisplay;
    const maxDiscount = getAutoshipMaxSavings();

    const isPrecrawl = autoshipFlag === 'precrawl';
    const autoshipDiscountHeader = getAutoshipDiscountHeader(displayDiscount);

    if (isPrecrawl) {
        const subtextMessage = shouldShowRecurringDiscount
            ? `Save ${displayDiscount} on future orders (up to ${maxDiscount})`
            : `Save ${displayDiscount} on your first order and ${AUTOSHIP_RECURRING_ORDERS_DISCOUNT} on future orders (up to ${maxDiscount})`;

        return {
            autoshipDiscountHeader,
            autoshipDiscountSubtext: sanitizeWhitespace(subtextMessage),
            autoshipDiscountSubtextTwo: undefined,
        };
    }

    const subtextMessage = shouldShowRecurringDiscount
        ? `Save ${AUTOSHIP_RECURRING_ORDERS_DISCOUNT} on all orders & FREE shipping over ${AUTOSHIP_FREE_SHIPPING_THRESHOLD}.`
        : `Save ${displayDiscount} on your first order (up to ${maxDiscount}).`;

    const autoshipDiscountSubtextTwo = shouldShowRecurringDiscount
        ? undefined
        : `Future orders get ${AUTOSHIP_RECURRING_ORDERS_DISCOUNT} off & FREE shipping over ${AUTOSHIP_FREE_SHIPPING_THRESHOLD}.`;

    return {
        autoshipDiscountHeader,
        autoshipDiscountSubtext: sanitizeWhitespace(subtextMessage),
        autoshipDiscountSubtextTwo,
    };
}

function getAutoshipDiscountHeader(discount: string): string {
    const hasDiscount = discount.trim();

    return hasDiscount ? `Autoship & save ${discount}` : 'Autoship & save';
}

function sanitizeWhitespace(text: string): string {
    return text.replace(/ {2,}/g, ' ');
}
